import { useState, useRef, useEffect } from "react";
import { Zap } from "lucide-react";
import { injectNewToken } from "@/lib/nurseryEngine";
import { TokenAvatar } from "@/components/TokenAvatar";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { useSocket } from "@/context/SocketContext";
import { useApp } from "@/context/AppCtx";

interface BundleToast {
  id: string;
  count: number;
  symbols: string[];
  tokens: { symbol: string; mint: string; imageUri?: string }[];
  expiresAt: number;
}

export function GlobalDeploymentToasts() {
  const { t } = useTranslation();
  const { socket } = useSocket();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { setCaMintInput } = useApp() as any;
  const [toasts, setToasts] = useState<BundleToast[]>([]);
  const seenRef = useRef(new Set<string>());
  // Pending mints buffered during the debounce window
  const pendingRef = useRef<{ symbol: string; mint: string; imageUri?: string }[]>([]);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const DEBOUNCE_MS = 1500;  // bundle window
  const DISPLAY_MS  = 5000;  // how long each toast stays visible

  const flushPending = () => {
    const batch = pendingRef.current.splice(0);
    if (batch.length === 0) return;

    const toastId = `bundle-${Date.now()}`;
    const expiresAt = Date.now() + DISPLAY_MS;
    const newToast: BundleToast = {
      id: toastId,
      count: batch.length,
      symbols: batch.map(b => b.symbol),
      tokens: batch.map(b => ({
        symbol: b.symbol,
        mint: b.mint,
        imageUri: b.imageUri,
      })),
      expiresAt,
    };

    setToasts(prev => [...prev, newToast]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== toastId));
    }, DISPLAY_MS);
  };

  useEffect(() => {
    if (!socket) return;

    const handleNewToken = (token: any) => {
      // 1. Inject immediately into client-side engine corpus
      injectNewToken(token);

      // 2. Queue for toast rendering
      if (!seenRef.current.has(token.mint)) {
        seenRef.current.add(token.mint);
        pendingRef.current.push({ symbol: token.symbol, mint: token.mint, imageUri: token.uri || token.imageUri });
        
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(flushPending, DEBOUNCE_MS);
      }
    };

    socket.on('server-new-token', handleNewToken);

    return () => {
      socket.off('server-new-token', handleNewToken);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [socket]);

  if (toasts.length === 0) return null;

  return (
    <div className="absolute top-16 left-0 right-0 z-50 flex flex-col items-center gap-2 pointer-events-none px-4">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          onClick={() => {
            if (toast.count === 1) {
              setCaMintInput(toast.tokens[0].mint);
            }
          }}
          className={`flex items-center gap-3 bg-black/80 backdrop-blur-md border border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.2)] rounded-2xl p-2 animate-in slide-in-from-top-4 fade-in pointer-events-auto hover:scale-105 transition-transform ${toast.count === 1 ? 'cursor-pointer' : ''}`}
          style={{ width: "min(400px, 100%)" }}
        >
          <div className="flex items-center -space-x-4 pl-2 shrink-0">
            {toast.tokens.slice(0, 8).map((token: any, i: number) => (
              <div 
                key={token.mint} 
                className="relative z-10 size-11 rounded-full overflow-hidden bg-black/50 border-2 border-[#0b0e11] shadow-lg"
                style={{ zIndex: 10 - i }}
              >
                <TokenAvatar mint={token.mint} imageUri={token.imageUri} symbol={token.symbol} size="size-11" />
              </div>
            ))}
            {toast.count > 8 && (
              <div className="relative size-11 rounded-full overflow-hidden bg-black/80 border-2 border-[#0b0e11] z-0 flex items-center justify-center shadow-[0_0_10px_rgba(16,185,129,0.3)]">
                <span className="text-xs font-black text-white">+{toast.count - 8}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-[12px] font-black text-white leading-tight truncate" title={toast.symbols.join(", ")}>
              {toast.count === 1 ? toast.symbols[0] : `${toast.symbols.slice(0, 4).join(", ")}${toast.count > 4 ? ` ${t("toast.more").replace("{count}", (toast.count - 4).toString())}` : ""}`}
            </span>
            <span className="text-[10px] font-bold text-emerald-400 flex items-center gap-1 truncate">
              <Zap className="size-3 shrink-0" />
              <span className="truncate">{toast.count === 1 ? t("toast.justDeployed") : t("toast.massDeployment").replace("{count}", toast.count.toString())}</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
